import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useWatch } from "react-hook-form";
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  InputGroup,
  FormText,
  FormFeedback,
  Container,
  CustomInput,
  Col
} from 'reactstrap';
import * as Yup from 'yup';
import clsx from 'clsx';
import { useField } from 'formik';
import InputWithLoader from './InputWithLoader';
import { useTranslation } from 'react-i18next';
import PhoneNumberInput from './PhoneNumberInput';
import Checkbox from './Checkbox';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

export const LegalParticipantSchema = Yup.object().shape({
  name: Yup.string()
  .required('Required'),
  label: Yup.string()
  .required('Required'),
  obligation: Yup.string(),
  representative: Yup.string()
  .required('Required'),
  email: Yup.string()
    .email('InvalidEmail')
    .required('Required'),
  phone: Yup.string(),
  requestAgreement: Yup.bool()
});

export const IndividualParticipantSchema = Yup.object().shape({
  name: Yup.string()
  .required('Required'),
  last_name: Yup.string()
  .required('Required'),
  obligation: Yup.string(),
  label: Yup.string()
  .required('Required'),
  email: Yup.string()
    .email('InvalidEmail')
    .required('Required'),
  phone: Yup.string(),
  requestAgreement: Yup.bool()
});

export const ParticipantSchema = Yup.lazy(participant => {
  if (participant.obligation === 'legal') {
    return LegalParticipantSchema;
  } 
  
  return IndividualParticipantSchema;
})

export const getParticipantSchema = ({ validateAgendaProperties = false, validatePhone = false } = {}) => Yup.lazy(participant => {
  const schema = {
      name: Yup.string()
      .required('Required'),
      obligation: Yup.string(),
      email: Yup.string()
        .email('InvalidEmail')
        .required('Required'),
      phone: Yup.string(),
  }
  if (participant.obligation === 'legal') {
      schema.representative = Yup.string().required('Required');
  }  else {
      schema.last_name = Yup.string().required('Required');
  }

  if (validatePhone) {
    schema.phone = Yup.string().required('Required');
  } else {
    schema.phone = Yup.string();
  }
  const agendaPropertiesKeys = Object.keys(participant.agendaProperties || {});
  if (agendaPropertiesKeys.length > 0 && validateAgendaProperties) {
      schema.agendaProperties = {}
      agendaPropertiesKeys.map(key => {
          if (key !== 'agendaId') {
              schema.agendaProperties[key] = Yup.string().required('Required');
          }
      })
      schema.agendaProperties = Yup.object().shape(schema.agendaProperties)
  }

  return Yup.object().shape(schema);
})

function ParticipantForm({header, loading, editable, isDynamic, name, errors = {}, touched = {}, shieldType, participant = {}, onChange, fillMyData, showFillMyData, onFillMyData, onRequestAgreement, onRemove}) {
  const { t } = useTranslation('common');
  const isFillMyDataChecked = fillMyData === name;
  const prefix = name ? `${name}.` : '';


  return (
    <Container className="p-4">
      <h5 className={clsx({ 'text-muted': !header && !participant?.label})}>
       <span className="font-weight-bold"> 
        { header || participant?.label || t('forms.role')} 
        { onRemove && editable &&
          <span className="ml-2 text-muted float-right" onClick={onRemove(participant._id)}>
          <small className="text-muted text-right pointer"><FontAwesomeIcon icon="times" size="sm" color="muted" className="mr-2" />{t('actions.delete')}</small> 
          </span>
        }
       </span>
      
        {showFillMyData && editable &&
          <Checkbox 
              label={t('actions.fillMyData')}
              className="ml-2 text-muted" 
              style={{fontSize: "14px"}}
              onClick={onFillMyData({name, checked: isFillMyDataChecked })}
              name={name}
              checked={isFillMyDataChecked}
          />
        }
        </h5>
     
      <Form className="row">
      { isDynamic && <Col sm={12}>
            <FormGroup className="mb-0 py-2">
                <InputGroup>
                
                        <CustomInput
                          name={`${prefix}obligation`} 
                          value="individual"
                          className="checkbox-accent ml-3"
                          onChange={onChange}
                          type="radio" 
                          label="Fisica"
                          id={`${prefix}obligation-individual`}
                          checked={participant.obligation === "individual"}
                        />
                  
                        <CustomInput
                          name={`${prefix}obligation`} 
                          value="legal"
                          className="checkbox-accent ml-3"
                          onChange={onChange}
                          type="radio" 
                          label="Moral"
                          id={`${prefix}obligation-legal`}
                          checked={participant.obligation === "legal"}
                        />
                
                
                  {/* <FormFeedback>{t(`errors.${errors.name}`)}</FormFeedback> */}
                </InputGroup>
            </FormGroup>
          </Col> }
        <Col lg={6} sm={6}>
          <FormGroup className="mb-0">
              {
                participant.obligation === 'legal' && 
                <Label className="mb-1" for={`${prefix}name`}>{t('forms.companyName')} <span className="text-accent">*</span></Label>
              }
              {
                participant.obligation === 'individual' && 
                <Label className="mb-1" for={`${prefix}name`}>{t('forms.firstName')} <span className="text-accent">*</span></Label>
              }
              <InputGroup>
                <InputWithLoader
                  type="text" 
                  className="form-control" 
                  id={`${prefix}name`}
                  name={`${prefix}name`}
                  placeholder={t('forms.firstName')}
                  onChange={onChange}  
                  value={participant.name}
                  disabled={!editable}
                  loading={loading}
                  invalid={errors.name && touched.name}
                />
                <FormFeedback>{t(`errors.${errors.name}`)}</FormFeedback>
              </InputGroup>
          </FormGroup>
        </Col>
        {
          participant.obligation === 'individual' && 
          <Col lg={6} sm={6}>
              <FormGroup className="mb-0">
                  <Label className="mb-1" for={`${prefix}last_name`}>{t('forms.lastName')} <span className="text-accent">*</span></Label>
                  <InputGroup>
                    <InputWithLoader 
                      type="text" 
                      className="form-control" 
                      id={`${prefix}last_name`}
                      name={`${prefix}last_name`}
                      placeholder={t('forms.lastName')}
                      onChange={onChange}  
                      disabled={!editable}
                      value={participant.last_name}
                      loading={loading}
                      invalid={errors.last_name && touched.last_name}
                    />
                    <FormFeedback>{t(`errors.${errors.last_name}`)}</FormFeedback>
                  </InputGroup>
              </FormGroup>
          </Col>
        }
        {
          participant.obligation === 'legal' && 
          <Col lg={6} sm={6}>
            <FormGroup className="mb-0">
                <Label className="mb-1" for={`${prefix}representative`}>{t('forms.representative')} <span className="text-accent">*</span></Label>
                <InputGroup>
                  <InputWithLoader 
                    type="text" 
                    className="form-control" 
                    id={`${prefix}representative`}
                    name={`${prefix}representative`}
                    placeholder={t('forms.representative')}
                    onChange={onChange}  
                    disabled={!editable}
                    value={participant.representative}
                    loading={loading}
                    invalid={errors.representative && touched.representative}
                  />
                  <FormFeedback>{t(`errors.${errors.representative}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
          </Col>
        }
        <Col lg={12} sm={12} className="my-3">
            <FormGroup className="mb-0">
                <Label className="mb-1" for={`${prefix}email`}>{t('forms.email')} <span className="text-accent">*</span></Label>
                  <InputGroup>
                    <InputWithLoader
                      type="email" 
                      className="form-control" 
                      id={`${prefix}email`} 
                      name={`${prefix}email`}
                      placeholder={t('forms.email')}
                      onChange={onChange}  
                      disabled={!editable}
                      value={participant.email}
                      loading={loading}
                      invalid={errors.email && touched.email}
                    />
                  <FormFeedback>{t(`errors.${errors.email}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
        </Col>
        <Col lg={6} sm={6}>
            <FormGroup className="form-group mb-0">
                <Label className="mb-1" for={`${prefix}phone`}>{t('forms.phoneNumber')}</Label>
                <InputGroup>
                  <PhoneNumberInput
                    className="form-control" 
                    id={`${prefix}phone`} 
                    name={`${prefix}phone`}
                    placeholder={t('forms.phoneNumber')}
                    onChange={onChange}  
                    disabled={!editable}
                    value={participant.phone}
                    loading={loading}
                    invalid={errors.phone && touched.phone}
                  />
                  <FormFeedback>{t(`errors.${errors.phone}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
        </Col>
        <Col lg={6} sm={6}>
            <FormGroup className="form-group mb-0">
                <Label className="mb-1" for={`${prefix}role`}>{t('forms.role')} <span className="text-accent">*</span></Label>
                <InputGroup>
                  <InputWithLoader 
                    type="text" 
                    className="form-control" 
                    id={`${prefix}label`}
                    placeholder={t('forms.role')}
                    name={`${prefix}label`}
                    onChange={onChange} 
                    disabled={!editable} 
                    value={participant.label}
                    loading={loading}
                    invalid={errors.label && touched.label}
                  />
                  <FormFeedback>{t(`errors.${errors.label}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
        </Col> 
        
        {!isFillMyDataChecked && editable &&
          <Col md={12} className="mt-2">
            <Checkbox 
              label={t('actions.requestAgreement')}
              className="text-muted" 
              style={{fontSize: "16px"}}
              onClick={onRequestAgreement(`${prefix}requestAgreement`, participant.requestAgreement)}
              name={name}
              checked={participant.requestAgreement}
            />
          </Col>
        }
      </Form>
    </Container>
  )
}


export function NewParticipantForm({ 
  header, 
  loading, 
  editable, 
  name, 
  index,
  errors = {}, 
  touched = {}, 
  shieldType, 
  participant = {}, 
  watchedParticipant,
  register, 
  control,
  fillMyData, 
  onFillMyData, 
  onRequestAgreement, 
  onParticipantUpdate,
  onRemove 
}) {
  const { t } = useTranslation('common');

  const isFillMyDataChecked = fillMyData === participant._id;
  const showFillMyData = ["", "reset"].includes(fillMyData) || isFillMyDataChecked;
  const prefix = name ? `${name}.` : '';

  if (participant.deleted) {
  return null;
  }

  const handleRemove = () => {
    onRemove(participant, index)
  }
  const handleObligationChange = (evt) => {
    handlePartcipantUpdate({ obligation: evt.target.value })
  }
  const handleRoleChange = (evt) => {
    handlePartcipantUpdate({ label: evt.target.value })
  }

  const handlePartcipantUpdate = (updatedProperties) => {
    onParticipantUpdate(participant, index, updatedProperties)
  }
  
  return (
    <Container className="p-4">
      <div className="d-flex justify-content-between">
        <div className="d-flex">
          <h5 className={clsx("font-weight-bold", { 'text-muted': !header && !participant?.label})}>
              { header && (participant?.label ?? t('forms.role') )} 
          </h5>
          {showFillMyData && editable &&
           
            <Controller 
                name={`fillMyData`}
                control={control}
                defaultValue={""}
                render={({ onChange, name, value }) =>( 
                    <CustomInput 
                      className="checkbox-accent ml-2 text-muted" 
                      name={name}
                      type="checkbox" 
                      label={<span style={{fontSize: "14px"}}>{t('actions.fillMyData')}</span>}
                      onChange={e => onChange(e.target.checked ? participant._id : "reset")}
                      id={`${prefix}fillMyData`} 
                      checked={participant._id === value}
                    />
                  )
                }
            />
          }
          {!isFillMyDataChecked && shieldType?.value === 'shield3' && editable &&
            <Controller 
                name={`${prefix}requestAgreement`}
                control={control}
                defaultValue={participant.requestAgreement === true}
                render={({ onChange, name, value }) =>( 
                    <CustomInput 
                      className="checkbox-accent ml-2 text-muted" 
                      name={name}
                      type="checkbox" 
                      label={<span style={{fontSize: "14px"}}>{t('actions.requestAgreement')}</span>}
                      onChange={e => onChange(e.target.checked)}
                      id={`${prefix}requestAgreement`} 
                      checked={value}
                    />
                  )
                }
            />
          }
        </div>
        { onRemove && editable &&
          <span className="ml-2 text-muted font-weight-bold" onClick={handleRemove}>
            <small className="text-muted text-right pointer"><FontAwesomeIcon icon="times" size="sm" color="muted" className="mr-2" />{t('actions.delete')}</small> 
          </span>
        }
      </div>
      <Form className="row">
        <input type="hidden" name={`${prefix}_id`} ref={register()} defaultValue={participant._id} />
        <input type="hidden" name={`${prefix}deleted`} ref={register()} defaultValue={participant.deleted} />
        <Col sm={12}>
            <FormGroup className="mb-0 py-2">
                <InputGroup>
                  <Controller 
                      // as={CustomInput}
                      control={control}
                      name={`${prefix}obligation`}
                      defaultValue="individual"
                      render={({name, value, checked, onChange}) => {
                        // console.log({name, value, checked})
                        return (
                        <CustomInput
                          name={name} 
                          value="individual"
                          className="checkbox-accent ml-3"
                          onChange={e => {
                            handleObligationChange(e, value)
                            onChange(e)
                          }}
                          type="radio" 
                          label="Fisica"
                          id={`${prefix}obligation-individual`}
                          defaultChecked={participant.obligation === "individual"}
                        />
                      )}}
                      // innerRef={register()}
                  />
                  <Controller 
                      // as={CustomInput}
                      control={control}
                      name={`${prefix}obligation`}
                      render={({name, value, onChange}) => (
                        <CustomInput
                          name={name} 
                          value="legal"
                          className="checkbox-accent ml-3"
                          onChange={e => {
                            handleObligationChange(e, value)
                            onChange(e)
                          }}
                          type="radio" 
                          label="Moral"
                          id={`${prefix}obligation-legal`}
                          checked={participant.obligation === "legal"}
                        />
                      )}
                      defaultValue="legal"
                      // innerRef={register()}
                  />
                
                
                  {/* <FormFeedback>{t(`errors.${errors.name}`)}</FormFeedback> */}
                </InputGroup>
            </FormGroup>
          </Col>
        <Col lg={6} sm={6}>
          <FormGroup className="mb-0">
              {
                participant.obligation === 'legal' && 
                <Label className="mb-1" for={`${prefix}name`}>{t('forms.companyName')} <span className="text-accent">*</span></Label>
              }
              {
                participant.obligation === 'individual' && 
                <Label className="mb-1" for={`${prefix}name`}>{t('forms.firstName')} <span className="text-accent">*</span></Label>
              }
              <InputGroup>
                <InputWithLoader
                  type="text" 
                  className="form-control" 
                  id={`${prefix}name`}
                  name={`${prefix}name`}
                  placeholder={t('forms.firstName')}
                  innerRef={register()} 
                  defaultValue={participant.name}
                  disabled={!editable}
                  loading={loading}
                  invalid={errors.name?.message}
                />
                <FormFeedback>{t(`errors.${errors.name?.message}`)}</FormFeedback>
              </InputGroup>
          </FormGroup>
        </Col>
        {
          participant.obligation === 'individual' && 
          <Col lg={6} sm={6}>
              <FormGroup className="mb-0">
                  <Label className="mb-1" for={`${prefix}last_name`}>{t('forms.lastName')} <span className="text-accent">*</span></Label>
                  <InputGroup>
                    
                    <InputWithLoader 
                      type="text" 
                      className="form-control" 
                      id={`${prefix}last_name`}
                      name={`${prefix}last_name`}
                      placeholder={t('forms.lastName')}
                      innerRef={register()} 
                      disabled={!editable}
                      defaultValue={participant.last_name}
                      loading={loading}
                      invalid={errors.last_name?.message}
                    />
                    <FormFeedback>{t(`errors.${errors.last_name?.message}`)}</FormFeedback>
                  </InputGroup>
              </FormGroup>
          </Col>
        }
        {
          participant.obligation === 'legal' && 
          <Col lg={6} sm={6}>
            <FormGroup className="mb-0">
                <Label className="mb-1" for={`${prefix}representative`}>{t('forms.representative')} <span className="text-accent">*</span></Label>
                <InputGroup>
                  <InputWithLoader 
                    type="text" 
                    className="form-control" 
                    id={`${prefix}representative`}
                    name={`${prefix}representative`}
                    placeholder={t('forms.representative')}
                    innerRef={register()} 
                    disabled={!editable}
                    defaultValue={participant.representative}
                    loading={loading}
                    invalid={errors.representative?.message}
                  />
                  <FormFeedback>{t(`errors.${errors.representative?.message}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
          </Col>
        }
        <Col lg={12} sm={12} className="my-3">
            <FormGroup className="mb-0">
                <Label className="mb-1" for={`${prefix}email`}>{t('forms.email')} <span className="text-accent">*</span></Label>
                  <InputGroup>
                    <InputWithLoader
                      type="email" 
                      className="form-control" 
                      id={`${prefix}email`} 
                      name={`${prefix}email`}
                      placeholder={t('forms.email')}
                      innerRef={register()} 
                      disabled={!editable}
                      defaultValue={participant.email}
                      loading={loading}
                      invalid={errors.email?.message}
                    />
                  <FormFeedback>{t(`errors.${errors?.email?.message}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
        </Col>
        <Col lg={6} sm={6}>
            <FormGroup className="form-group mb-0">
                <Label className="mb-1" for={`${prefix}phone`}>{t('forms.phoneNumber')}</Label>
                <InputGroup>
                  <Controller 
                    as={PhoneNumberInput}
                    control={control}
                    className="form-control" 
                    id={`${prefix}phone`} 
                    name={`${prefix}phone`}
                    placeholder={t('forms.phoneNumber')}
                    disabled={!editable}
                    defaultValue={participant.phone}
                    loading={loading}
                    invalid={errors.phone?.message}
                  />
                  <FormFeedback>{t(`errors.${errors?.phone?.message}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
        </Col>
        <Col lg={6} sm={6}>
            <FormGroup className="form-group mb-0">
                <Label className="mb-1" for={`${prefix}role`}>{t('forms.role')} <span className="text-accent">*</span></Label>
                <InputGroup>
                  <InputWithLoader 
                    type="text" 
                    className="form-control" 
                    id={`${prefix}label`}
                    placeholder={t('forms.role')}
                    name={`${prefix}label`}
                    onChange={handleRoleChange}
                    innerRef={register()}
                    disabled={!editable} 
                    defaultValue={participant.label}
                    loading={loading}
                    invalid={errors.label?.message}
                  />
                  <FormFeedback>{t(`errors.${errors?.label?.message}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
        </Col> 
      </Form>
    </Container>
  )
}

ParticipantForm.propTypes = {

}

export default ParticipantForm

